import { createEvent } from 'FormSwiftAPI/Event/EventActions';
import { SelfServeBuilderPopupTypes } from 'Popups';
import PopupConstants, { ImageTypes } from 'Popups/PopupConstants';

const uuidV4 = require('uuid/v4');

export * from 'Animation/AnimationActions';
export * from 'FormSwiftAPI/Session/SessionActions';
export * from 'FormSwiftAPI/Session/SessionAutoSaveActions';
export * from 'FreeWriteBuilder/FreeWriteBuilderActions';
export * from 'FreeWriteBuilderModals/FreeWriteBuilderModalActions';
export * from './download-overlay/DownloadOverlayActions';
export * from './EditorOverlay/EditorOverlayActions';
export * from './SignatureManager/SignatureManagerActions';
export * from './SessionList/SessionListActions';
export * from './StaticBuilder/StaticBuilderActions';
export * from './VisualStatic/VisualStaticActions';
export * from './Modals/ModalActions';
export * from './Upload/UploadActions';
export * from './RulerTool/RulerToolActions';
export { enableRecipientSignature } from './Popups/RequestSignaturePopupWrapper/RequestSignatureActions';

export const documentSettingsInitialized = documentSettings => dispatch =>
  dispatch({ type: 'DOCUMENT_SETTINGS_INITIALIZED', documentSettings });

export function setZIndexPopup(zIndex) {
  return (dispatch) => {
    dispatch({ type: 'SET_Z_INDEX', zIndex });
  };
}

export function updateTextareaState(attributes) {
  return (dispatch) => {
    dispatch({ type: 'UPDATE_TEXTAREA_STATE', attributes });
  };
}

export function updateImageState(attributes) {
  return (dispatch) => {
    dispatch({ type: 'UPDATE_IMAGE_STATE', attributes });
  };
}

export function clearActivePopup() {
  return (dispatch) => {
    dispatch({ type: 'CLEAR_ACTIVE_POPUP' });
  };
}

export function setActivePopup(id) {
  return (dispatch) => {
    dispatch({ type: 'ACTIVE_POPUP_SET', id });
  };
}

export function beginEditingPopup() {
  return (dispatch) => {
    dispatch({ type: 'EDIT_POPUP_START' });
  };
}

export function endEditingPopup() {
  return (dispatch) => {
    dispatch({ type: 'EDIT_POPUP_END' });
  };
}

export const movePopup = (id, x, y) => (
  (dispatch) => {
    dispatch({ type: 'MOVE_POPUP', id, x, y });
  }
);

export const deletePopup = id => (
  (dispatch) => {
    dispatch({ type: 'DELETE_POPUP', id });
  }
);

export const deleteGroupPopup = (id, activeId) => (
  (dispatch) => {
    dispatch({ type: 'GROUP_POPUP_DELETE', id, activeId });
  }
);

export const changePopup = (id, attributes) => (
  (dispatch) => {
    dispatch({ type: 'CHANGE_POPUP', id, attributes });
  }
);

export const unwrapRequestSignaturePopups = () => (
  (dispatch) => {
    dispatch({ type: 'UNWRAP_REQUEST_SIGNATURE_POPUPS' });
  }
);

/**
 * Remove share popups.  If #signer is specified, remove only popups assigned to that signer.
 * @param signer {string} OPTIONAL email address for signer
 */
export const deleteSharePopups = signer => (
  (dispatch) => {
    dispatch({ type: 'DELETE_SHARE_POPUPS', signer });
  }
);

export function createTextBox(
  pageIdx,
  x = 0,
  y = 0,
  width = 330,
  height = 30,
) {
  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    dispatch(
      {
        type: 'CREATE_POPUP',
        id,
        data: {
          id,
          type: SelfServeBuilderPopupTypes.TEXTBOX,
          x,
          y,
          width,
          height,
          pageIdx,
          label: '',
          timestamp: Date.now(),
        },
      },
    );
  };
}

export function createSignature(
  pageIdx,
  x = 0,
  y = 0,
  width = 343,
  height = 30,
) {
  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    dispatch(
      {
        type: 'CREATE_POPUP',
        id,
        data: {
          id,
          type: SelfServeBuilderPopupTypes.SIGNATURE,
          x,
          y,
          width,
          height,
          pageIdx,
          label: 'Signature',
          timestamp: Date.now(),
        },
      },
    );
  };
}

export function createGroupPopup(
  pageIdx,
  groupId,
  type,
  x = 0,
  y = 0,
  width = 30,
  height = 30,
) {
  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    return dispatch(
      {
        type: 'CREATE_POPUP',
        id,
        data: {
          id,
          type,
          x,
          y,
          width,
          height,
          pageIdx,
          label: '',
          timestamp: Date.now(),
          groupId,
        },
      },
    );
  };
}

export function createImgPopup(
  imageUrl,
  imgIdx,
  pageIdx = window.getPageIndex(),
  width = 300,
  x = 0,
  y = 0,
  draggable = true,
  disabled = false,
) {
  if (!imageUrl) {
    return (dispatch) => {
      dispatch({ type: 'PASS' });
    };
  }

  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    dispatch(
      {
        type: 'CREATE_POPUP',
        id,
        attachToMouse: true,
        data: {
          id,
          type: 'image',
          imageUrl,
          imgIdx,
          pageIdx,
          width,
          x,
          y,
          draggable,
          disabled,
        },
      },
    );
  };
}

export const createLogoPopup = (
  imageUrl,
  imgIdx,
  width = 300,
  x = 0,
  y = 0,
  draggable = true,
  disabled = false,
) => async (dispatch) => {
  if (!imageUrl) {
    return dispatch({ type: 'PASS' });
  }
  const id = uuidV4();
  return dispatch({
    type: 'CREATE_POPUP',
    id,
    data: {
      id,
      type: 'logo',
      imageUrl,
      imgIdx,
      width,
      x,
      y,
      draggable,
      disabled,
      isGlobal: true,
    },
  });
};

export function createResizablePopup(
  imageUrl,
  type,
  imgIdx,
  pageIdx = window.getPageIndex(),
  width,
  height,
  x,
  y,
  draggable = false,
  disabled = false,
  hovering = false,
  resizable = true,
) {
  if (!imageUrl) {
    return (dispatch) => {
      dispatch({ type: 'PASS' });
    };
  }

  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    dispatch({
      type: 'CREATE_POPUP',
      id,
      data: {
        id,
        type,
        imageUrl,
        imgIdx,
        pageIdx,
        width,
        height,
        x,
        y,
        draggable,
        disabled,
        hovering,
        resizable,
      },
    });
  };
}

export function createDraggableResizablePopup(
  imageUrl,
  type,
  imgIdx,
  pageIdx = window.getPageIndex(),
  width,
  height,
  x,
  y,
  draggable = true,
  disabled = false,
  hovering = false,
  resizable = true,
) {
  return createResizablePopup(imageUrl, type, imgIdx, pageIdx, width,
                              height, x, y, draggable, disabled, hovering, resizable);
}

export function createCheckboxPopup(
  x = 0,
  y = 0,
  width = 28,
  height = 28,
  pageIdx = window.getPageIndex(),
  checked = false,
  disabled = false,
) {
  return (dispatch) => {
    const id = uuidV4();

    // make an api call
    dispatch({
      type: 'CREATE_POPUP',
      id,
      data: {
        id,
        type: 'checkbox',
        draggable: false,
        x,
        y,
        width,
        height,
        pageIdx,
        checked,
        disabled,
      },
    });
  };
}


export function createTextPopup({
  x = 0,
  y = 0,
  width = 12,
  height = 28,
  pageIdx = window.getPageIndex(),
  text = '',
  bold = false,
  italic = false,
  fontFamily = 'Arial',
  fontSize = 17,
  color = '#000000', // Needs to be 6 digits on the backend
  draggable = true,
  disabled = false,
  backgroundColor = null,
}) {
  return (dispatch, getState) => {
    const id = uuidV4();
    const state = getState();
    const zIndex = state.popupHandler.zIndex;

    // make an api call
    dispatch({
      type: 'CREATE_POPUP',
      id,
      data: {
        id,
        type: 'text',
        x,
        y,
        width,
        height,
        pageIdx,
        text,
        bold,
        italic,
        fontFamily,
        fontSize,
        color,
        draggable,
        disabled,
        zIndex,
        backgroundColor,
      },
    });
  };
}

export function createRequestSignaturePopup(
  requestType,
  x = 0,
  y = 0,
  width = 125,
  height = 40,
  pageIdx = window.getPageIndex(),
  draggable = true,
  disabled = false,
  resizable = true,
  requestSettings = {},
  contentSettings = {},
) {
  return (dispatch, getState) => {
    const id = uuidV4();
    const state = getState();
    const zIndex = state.popupHandler.zIndex;
    dispatch({
      type: 'CREATE_POPUP',
      id,
      data: {
        id,
        type: 'request-signature',
        requestType,
        x,
        y,
        width,
        height,
        pageIdx,
        draggable,
        disabled,
        zIndex,
        resizable,
        requestSettings,
        contentSettings,
      },
    });
  };
}

export function createSigPopup(
  imageUrl,
  imgIdx,
  pageIdx = window.getPageIndex(),
  width = 300,
  x = 0,
  y = 0,
  draggable = true,
  disabled = false,
) {
  if (!imageUrl) {
    return (dispatch) => {
      dispatch({ type: 'PASS' });
    };
  }

  let sigWidth = width;
  if (width > 300) {
    sigWidth = 300;
  }

  return (dispatch) => {
    const id = uuidV4();
    // make an api call
    dispatch({
      type: 'CREATE_POPUP',
      id,
      data: {
        id,
        type: 'image',
        width: sigWidth,
        imageUrl,
        imgIdx,
        pageIdx,
        x,
        y,
        draggable,
        disabled,
        imgType: ImageTypes.SIGNATURE,
      },
    });
  };
}

export const updateRequestSignatureContent = (popupID, imgSrc, sigType = null) => (
  (dispatch) => {
    dispatch({ type: 'UPDATE_REQUEST_SIGNATURE_CONTENT', popupID, imgSrc, sigType });
  }
);

export const createSignatureEvent = (category, action, userId = undefined) => (
  (dispatch) => {
    dispatch(createEvent(category, action, userId));
  }
);

export const setPopupAttachedToMouse = isPopupAttachedToMouse => (
  (dispatch) => {
    dispatch({ type: PopupConstants.ATTACH_TO_MOUSE_SET, isPopupAttachedToMouse });
  }
);

export const mainContainerHeightAdjusted = newHeight => (
  (dispatch) => {
    dispatch({ type: 'MAIN_CONTAINER_HEIGHT_ADJUSTED', height: newHeight });
  }
);



// WEBPACK FOOTER //
// ./src-react/actions.jsx